import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Leaf, MapPin, Clock, Calendar, Users, Upload, Send, CheckCircle2 } from "lucide-react";
import { districtFromPostalCode, formatPostalCode } from "@/lib/postalCodes";

const AVAILABILITY_TAGS = [
  "Imediata", "Manhã", "Tarde", "Noite", "24h / Interna", "Fins de semana",
];

const EXPERIENCE_TAGS = [
  "Higiene pessoal",
  "Preparação de refeições",
  "Administração de medicação",
  "Mobilidade / transferências",
  "Companhia",
  "Cuidados a acamados",
  "Demência / Alzheimer",
];

const YEARS_TAGS = [
  "Sem experiência",
  "Menos de 1 ano",
  "1–2 anos",
  "3–5 anos",
  "6–10 anos",
  "Mais de 10 anos",
];

function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function PublicApply() {
  const { shareCode } = useParams();
  const [params] = useSearchParams();
  const urlRef = (params.get("ref") || "").toUpperCase();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [successDistrict, setSuccessDistrict] = useState([]);
  const [duplicate, setDuplicate] = useState(null);
  const [showOtherVacancies, setShowOtherVacancies] = useState(false);
  const [manualRefDraft, setManualRefDraft] = useState("");
  const [manualRef, setManualRef] = useState("");
  const [availabilityTags, setAvailabilityTags] = useState([]);
  const [experienceTags, setExperienceTags] = useState([]);
  const [yearsTag, setYearsTag] = useState("");
  const [postalCode, setPostalCode] = useState("");
  const suggestedDistrict = districtFromPostalCode(postalCode);
  const [locationInfo, setLocationInfo] = useState({ concelho: "", distrito: "", loading: false });
  const [form, setForm] = useState({
    name: "", phone: "", email: "", locality: "",
    cv_data: null, cv_filename: "", consent: false,
  });
  const [busy, setBusy] = useState(false);

  // Auto-fill concelho + distrito from GeoAPI when a full CP7 (NNNN-NNN) is typed
  useEffect(() => {
    const digits = postalCode.replace(/\D/g, "");
    if (digits.length !== 7) {
      setLocationInfo((s) => ({ ...s, concelho: "", distrito: districtFromPostalCode(postalCode) || "" }));
      return;
    }
    let cancel = false;
    setLocationInfo((s) => ({ ...s, loading: true }));
    const cp = `${digits.slice(0, 4)}-${digits.slice(4)}`;
    // GeoAPI.pt is free, no key required, CORS enabled
    fetch(`https://json.geoapi.pt/cp/${cp}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((json) => {
        if (cancel) return;
        const concelho = json?.Concelho || "";
        const distrito = json?.Distrito || districtFromPostalCode(postalCode) || "";
        setLocationInfo({ concelho, distrito, loading: false });
      })
      .catch(() => {
        if (cancel) return;
        setLocationInfo({ concelho: "", distrito: districtFromPostalCode(postalCode) || "", loading: false });
      });
    return () => { cancel = true; };
  }, [postalCode]);

  // Effective referrer code: prefer URL param, fallback to manual entry (committed)
  const ref = (urlRef || manualRef);

  useEffect(() => {
    api.get(`/apply/${shareCode}${urlRef ? `?ref=${urlRef}` : ""}`)
      .then(({ data }) => setData(data))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Erro ao carregar vaga"))
      .finally(() => setLoading(false));
  }, [shareCode, urlRef]);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value }));

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) { toast.error("CV excede 4 MB"); return; }
    const b64 = await toBase64(file);
    setForm((f) => ({ ...f, cv_data: b64, cv_filename: file.name }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!ref) { toast.error("Introduza o código do referenciador"); return; }
    if (!postalCode || (!suggestedDistrict && !locationInfo.distrito)) { toast.error("Indique o código postal (formato NNNN-NNN)"); return; }
    if (availabilityTags.length === 0) { toast.error("Selecione pelo menos uma opção de disponibilidade"); return; }
    if (!yearsTag) { toast.error("Indique os anos de experiência como ajudante familiar"); return; }
    if (experienceTags.length === 0) { toast.error("Selecione pelo menos uma área de experiência"); return; }
    if (!form.consent) { toast.error("Precisa de aceitar o consentimento RGPD"); return; }
    setBusy(true);
    try {
      const distrito = locationInfo.distrito || suggestedDistrict;
      const concelho = locationInfo.concelho;
      const locBits = [];
      if (form.locality?.trim()) locBits.push(form.locality.trim());
      if (concelho) locBits.push(concelho);
      if (distrito && distrito !== concelho) locBits.push(distrito);
      const payload = {
        ...form,
        locality: locBits.join(", ") || distrito,
        postal_code: postalCode,
        availability: availabilityTags.join(", "),
        experience: `Ajudante familiar: ${yearsTag} · Áreas: ${experienceTags.join(", ")}`,
        referrer_code: ref,
      };
      const { data: resp } = await api.post(`/apply/${shareCode}`, payload);
      setSuccessDistrict(resp?.district_vacancies || []);
      setSubmitted(true);
    } catch (err) {
      const resp = err.response?.data;
      if (err.response?.status === 409 && resp?.duplicate) {
        setDuplicate({
          existing: resp.existing_vacancy,
          districtVacancies: resp.district_vacancies || [],
        });
      } else {
        toast.error(formatApiError(resp?.detail) || err.message);
      }
    } finally { setBusy(false); }
  };

  const toggleTag = (list, setList, tag) => {
    setList(list.includes(tag) ? list.filter(t => t !== tag) : [...list, tag]);
  };

  if (loading) return <div className="min-h-screen grid place-items-center text-[var(--ink-soft)]">A carregar…</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-[var(--ink-soft)]">Vaga não encontrada.</div>;

  const v = data.vacancy;
  const isContinuous = v.service_type === "continuous";
  const remaining = Math.max((v.slots || 1) - (v.slots_filled || 0), 0);
  const isClosed = v.status !== "open";
  const inputCls = "w-full px-3 py-2.5 rounded-xl bg-[var(--muted)] border border-[var(--border)] focus:outline-none focus:ring-2 focus:ring-[var(--forest)]";

  return (
    <div className="min-h-screen bg-[var(--bone)] paper-noise">
      <header className="glass-nav">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-[var(--forest)] text-white grid place-items-center"><Leaf className="w-5 h-5"/></div>
            <div>
              <div className="font-heading text-lg text-[var(--forest)]">CARING</div>
              <div className="eyebrow -mt-0.5">Recrutamento</div>
            </div>
          </div>
          {data.referrer && (
            <div className="text-right text-xs text-[var(--ink-soft)]">
              Recomendação de<br/><strong className="text-[var(--ink)]">{data.referrer.display_name}</strong>
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-12">
        {submitted ? (
          <div className="max-w-3xl mx-auto space-y-6" data-testid="apply-success">
            <div className="card-surface p-10 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[var(--success)] text-white grid place-items-center mx-auto">
                <CheckCircle2 className="w-7 h-7"/>
              </div>
              <h1 className="font-heading text-3xl mt-4">Candidatura recebida</h1>
              <p className="text-[var(--ink-soft)] mt-2">Obrigado por se candidatar à CARING. A nossa equipa de recrutamento vai contactá-lo(a) brevemente através dos dados que forneceu.</p>
            </div>
            {successDistrict.length > 0 && (
              <div className="space-y-4" data-testid="success-district-list">
                <div className="eyebrow px-1">Outras oportunidades na sua área</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {successDistrict.map((ov) => (
                    <div key={ov.id} className="card-surface p-5" data-testid={`success-other-${ov.share_code}`}>
                      <span className={`chip ${ov.service_type === "continuous" ? "chip-green" : "chip-blue"}`}>
                        {ov.service_type === "continuous" ? "🟢 Continuidade" : "🔵 Pontual"}
                      </span>
                      <h3 className="font-heading text-xl mt-3 leading-tight break-words">{ov.title}</h3>
                      <div className="mt-3 space-y-1 text-sm text-[var(--ink-soft)]">
                        <div className="flex items-center gap-2"><MapPin className="w-4 h-4"/> {ov.locality}</div>
                        <div className="flex items-center gap-2"><Clock className="w-4 h-4"/> {ov.schedule}</div>
                      </div>
                      <Link
                        to={`/apply/${ov.share_code}${ref ? `?ref=${ref}` : ""}`}
                        className="pill-btn pill-ghost !py-2 !px-3 mt-4"
                      >
                        Ver esta vaga
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : duplicate ? (
          <div className="max-w-3xl mx-auto space-y-6" data-testid="apply-duplicate">
            <div className="card-surface p-8 sm:p-10 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[rgba(217,108,74,0.12)] text-[var(--terracotta)] grid place-items-center mx-auto">
                <CheckCircle2 className="w-7 h-7"/>
              </div>
              <h1 className="font-heading text-3xl mt-4">Já se candidatou anteriormente</h1>
              {duplicate.existing ? (
                <p className="text-[var(--ink-soft)] mt-2">
                  Encontrámos uma candidatura sua para a vaga <strong className="text-[var(--ink)]">{duplicate.existing.title}</strong>{duplicate.existing.locality ? ` — ${duplicate.existing.locality}` : ""}. A CARING vai contactá-lo(a) através dos dados que forneceu nessa candidatura.
                </p>
              ) : (
                <p className="text-[var(--ink-soft)] mt-2">Já existe uma candidatura registada com este email ou telefone. A CARING vai contactá-lo(a) brevemente.</p>
              )}
              {duplicate.districtVacancies && duplicate.districtVacancies.length > 0 && !showOtherVacancies && (
                <button
                  type="button"
                  onClick={() => setShowOtherVacancies(true)}
                  className="pill-btn pill-primary mt-6"
                  data-testid="apply-see-other-vacancies"
                >
                  Ver outras vagas na sua localidade
                </button>
              )}
              {duplicate.districtVacancies && duplicate.districtVacancies.length === 0 && (
                <p className="text-xs text-[var(--ink-soft)] mt-4">De momento não há outras vagas abertas na sua localidade.</p>
              )}
            </div>

            {showOtherVacancies && duplicate.districtVacancies?.length > 0 && (
              <div className="space-y-4" data-testid="apply-other-vacancies-list">
                <div className="eyebrow px-1">Outras oportunidades na sua área</div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {duplicate.districtVacancies.map((ov) => (
                    <div key={ov.id} className="card-surface p-5" data-testid={`other-vacancy-${ov.share_code}`}>
                      <span className={`chip ${ov.service_type === "continuous" ? "chip-green" : "chip-blue"}`}>
                        {ov.service_type === "continuous" ? "🟢 Continuidade" : "🔵 Pontual"}
                      </span>
                      <h3 className="font-heading text-xl mt-3 leading-tight break-words">{ov.title}</h3>
                      <div className="mt-3 space-y-1 text-sm text-[var(--ink-soft)]">
                        <div className="flex items-center gap-2"><MapPin className="w-4 h-4"/> {ov.locality}</div>
                        <div className="flex items-center gap-2"><Clock className="w-4 h-4"/> {ov.schedule}</div>
                      </div>
                      <div className="mt-3">
                        {ov.service_type === "continuous"
                          ? <div className="font-heading text-lg text-[var(--success)]">{ov.monthly_avg ? `${Number(ov.monthly_avg).toFixed(0)} €/mês` : "—"}</div>
                          : <div className="font-heading text-lg text-[var(--info)]">{ov.total_value ? `${Number(ov.total_value).toFixed(0)} €` : "—"}</div>}
                      </div>
                      <Link
                        to={`/apply/${ov.share_code}${ref ? `?ref=${ref}` : ""}`}
                        className="pill-btn pill-ghost !py-2 !px-3 mt-4"
                        data-testid={`open-other-vacancy-${ov.share_code}`}
                      >
                        Ver esta vaga
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-6 lg:gap-8">
            <aside className="lg:col-span-1 space-y-4 min-w-0">
              <div className="min-w-0">
                <span className={`chip ${isContinuous ? "chip-green" : "chip-blue"}`}>
                  {isContinuous ? "🟢 Continuidade" : "🔵 Pontual"}
                </span>
                <h1 className="font-heading text-3xl mt-3 text-[var(--ink)] break-words" data-testid="apply-title">{v.title}</h1>
                {isClosed && <div className="mt-3 chip chip-neutral">Fechada</div>}
              </div>
              <div className="card-surface p-5 space-y-2 text-sm min-w-0">
                <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[var(--forest)] flex-shrink-0"/> {v.locality}</div>
                <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--forest)] flex-shrink-0"/> {v.schedule}</div>
                {v.hours && <div className="flex items-center gap-2"><Clock className="w-4 h-4 text-[var(--forest)] flex-shrink-0"/> {v.hours}</div>}
                {v.start_date && <div className="flex items-center gap-2"><Calendar className="w-4 h-4 text-[var(--forest)] flex-shrink-0"/> Início {v.start_date}</div>}
                <div className="flex items-center gap-2"><Users className="w-4 h-4 text-[var(--forest)] flex-shrink-0"/> {remaining} vaga{remaining !== 1 ? "s" : ""}</div>
              </div>
              {isContinuous ? (
                v.monthly_avg ? (
                  <div className="card-surface p-5 min-w-0">
                    <div className="eyebrow !text-[var(--success)]">Remuneração média mensal</div>
                    <div className="font-heading text-3xl text-[var(--success)] mt-1">{`${Number(v.monthly_avg).toFixed(0)} €/mês`}</div>
                  </div>
                ) : null
              ) : (
                v.total_value ? (
                  <div className="card-surface p-5 min-w-0">
                    <div className="eyebrow !text-[var(--info)]">Valor total do serviço</div>
                    <div className="font-heading text-3xl text-[var(--info)] mt-1">{`${Number(v.total_value).toFixed(0)} €`}</div>
                  </div>
                ) : null
              )}
              <div className="card-surface p-5 min-w-0">
                <div className="eyebrow">Requisitos</div>
                <p className="text-sm text-[var(--ink)] whitespace-pre-wrap mt-2 leading-relaxed break-words">{v.requirements}</p>
              </div>
            </aside>

            <div className="lg:col-span-2 min-w-0">
              {isClosed ? (
                <div className="card-surface p-10 text-center">
                  <p className="text-[var(--ink-soft)]">Esta vaga já foi preenchida e deixou de aceitar candidaturas.</p>
                  <button disabled className="pill-btn pill-primary mt-4 opacity-50" data-testid="apply-submit-btn">Enviar candidatura</button>
                </div>
              ) : !ref ? (
                <div className="card-surface p-6 sm:p-8 space-y-4">
                  <div>
                    <div className="eyebrow">Código do referenciador</div>
                    <h2 className="font-heading text-2xl mt-1">Falta um dado no seu link</h2>
                    <p className="text-sm text-[var(--ink-soft)] mt-2">Peça à pessoa que lhe partilhou esta oportunidade para lhe dar o <strong>código de referenciador</strong> (8 caracteres). Introduza-o abaixo para poder submeter a candidatura.</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Código do referenciador</label>
                    <input
                      value={manualRefDraft}
                      onChange={(e) => setManualRefDraft(e.target.value.toUpperCase())}
                      placeholder="Ex: A1B2C3D4"
                      maxLength={16}
                      className={`mt-1.5 ${inputCls} font-mono tracking-widest uppercase`}
                      data-testid="apply-manual-ref-input"
                    />
                    <div className="text-[10px] text-[var(--ink-soft)] mt-1">Este código garante que a candidatura é atribuída ao referenciador correcto.</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      const trimmed = manualRefDraft.trim().toUpperCase();
                      if (!trimmed) { toast.error("Introduza o código do referenciador"); return; }
                      setManualRef(trimmed);
                    }}
                    disabled={!manualRefDraft.trim()}
                    className="pill-btn pill-primary disabled:opacity-50"
                    data-testid="apply-manual-ref-continue"
                  >
                    Continuar
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="card-surface p-6 sm:p-8 space-y-5 min-w-0 overflow-hidden">
                  <div>
                    <div className="eyebrow">Candidatar-se</div>
                    <h2 className="font-heading text-2xl mt-1">Envie a sua candidatura</h2>
                    <p className="text-sm text-[var(--ink-soft)] mt-1">Preenchimento em 2 minutos. A CARING contacta-o depois pelos dados fornecidos.</p>
                  </div>
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="min-w-0"><label className="text-sm font-medium">Nome</label><input required value={form.name} onChange={update("name")} className={`mt-1.5 ${inputCls}`} data-testid="apply-name-input"/></div>
                    <div className="min-w-0"><label className="text-sm font-medium">Telefone</label><input required value={form.phone} onChange={update("phone")} className={`mt-1.5 ${inputCls}`} data-testid="apply-phone-input"/></div>
                    <div className="min-w-0"><label className="text-sm font-medium">Email</label><input required type="email" value={form.email} onChange={update("email")} className={`mt-1.5 ${inputCls}`} data-testid="apply-email-input"/></div>
                    <div className="min-w-0">
                      <label className="text-sm font-medium">Código postal</label>
                      <input
                        required
                        value={postalCode}
                        onChange={(e) => setPostalCode(formatPostalCode(e.target.value))}
                        placeholder="1000-100"
                        maxLength={8}
                        inputMode="numeric"
                        className={`mt-1.5 ${inputCls}`}
                        data-testid="apply-postal-input"
                      />
                      {postalCode && (locationInfo.concelho || locationInfo.distrito || suggestedDistrict) && (
                        <div className="text-xs text-[var(--forest)] mt-1 flex items-center gap-1 flex-wrap" data-testid="apply-district-suggestion">
                          <MapPin className="w-3 h-3"/>
                          {locationInfo.concelho ? (
                            <>Concelho: <strong>{locationInfo.concelho}</strong>{" · "}Distrito: <strong>{locationInfo.distrito || suggestedDistrict}</strong></>
                          ) : locationInfo.loading ? (
                            <>a detectar concelho e distrito…</>
                          ) : (
                            <>Distrito detectado: <strong>{locationInfo.distrito || suggestedDistrict}</strong></>
                          )}
                        </div>
                      )}
                      {postalCode && !suggestedDistrict && !locationInfo.distrito && postalCode.replace(/\D/g, "").length >= 4 && (
                        <div className="text-xs text-[var(--terracotta)] mt-1">Código postal não reconhecido — verifique.</div>
                      )}
                    </div>
                    <div className="md:col-span-2 min-w-0">
                      <label className="text-sm font-medium">Freguesia / bairro <span className="text-xs text-[var(--ink-soft)]">(opcional)</span></label>
                      <input value={form.locality} onChange={update("locality")} placeholder="Ex: Alvalade, Cedofeita, Sé…" className={`mt-1.5 ${inputCls}`} data-testid="apply-locality-input"/>
                    </div>

                    <div className="md:col-span-2 min-w-0">
                      <label className="text-sm font-medium">Disponibilidade</label>
                      <div className="text-[10px] text-[var(--ink-soft)] mb-2">Toque nas opções que se aplicam</div>
                      <div className="flex flex-wrap gap-2" data-testid="apply-availability-tags">
                        {AVAILABILITY_TAGS.map(tag => {
                          const on = availabilityTags.includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => toggleTag(availabilityTags, setAvailabilityTags, tag)}
                              className={`px-3 py-1.5 rounded-full text-sm border transition ${on ? "bg-[var(--forest)] text-white border-[var(--forest)]" : "bg-white border-[var(--border)] hover:border-[var(--forest)]"}`}
                              data-testid={`tag-avail-${tag}`}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="md:col-span-2 min-w-0">
                      <label className="text-sm font-medium">Anos de experiência como ajudante familiar</label>
                      <div className="text-[10px] text-[var(--ink-soft)] mb-2">Escolha uma opção</div>
                      <div className="flex flex-wrap gap-2" data-testid="apply-years-tags">
                        {YEARS_TAGS.map(tag => {
                          const on = yearsTag === tag;
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => setYearsTag(on ? "" : tag)}
                              className={`px-3 py-1.5 rounded-full text-sm border transition ${on ? "bg-[var(--forest)] text-white border-[var(--forest)]" : "bg-white border-[var(--border)] hover:border-[var(--forest)]"}`}
                              data-testid={`tag-years-${tag}`}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="md:col-span-2 min-w-0">
                      <label className="text-sm font-medium">Áreas de apoio domiciliário</label>
                      <div className="text-[10px] text-[var(--ink-soft)] mb-2">Toque em todas as áreas onde já trabalhou</div>
                      <div className="flex flex-wrap gap-2" data-testid="apply-experience-tags">
                        {EXPERIENCE_TAGS.map(tag => {
                          const on = experienceTags.includes(tag);
                          return (
                            <button
                              key={tag}
                              type="button"
                              onClick={() => toggleTag(experienceTags, setExperienceTags, tag)}
                              className={`px-3 py-1.5 rounded-full text-sm border transition ${on ? "bg-[var(--forest)] text-white border-[var(--forest)]" : "bg-white border-[var(--border)] hover:border-[var(--forest)]"}`}
                              data-testid={`tag-exp-${tag}`}
                            >
                              {tag}
                            </button>
                          );
                        })}
                      </div>
                    </div>

                    <div className="md:col-span-2 min-w-0">
                      <label className="text-sm font-medium">CV <span className="text-xs text-[var(--ink-soft)]">(PDF/imagem, máx 4 MB — opcional)</span></label>
                      <div className="file-drop mt-1.5 overflow-hidden">
                        <input type="file" accept=".pdf,image/*,.doc,.docx" onChange={onFile} className="text-xs sm:text-sm w-full max-w-full" data-testid="apply-cv-input"/>
                        {form.cv_filename && <div className="text-xs text-[var(--forest)] mt-2 flex items-center gap-1 break-all"><Upload className="w-3 h-3 flex-shrink-0"/> {form.cv_filename}</div>}
                      </div>
                    </div>
                  </div>

                  <label className="flex items-start gap-3 pt-2">
                    <input type="checkbox" checked={form.consent} onChange={update("consent")} className="mt-1" data-testid="apply-consent-checkbox"/>
                    <span className="text-sm text-[var(--ink-soft)]">Autorizo a CARING a tratar os meus dados para efeitos de recrutamento, nos termos do RGPD.</span>
                  </label>

                  <button disabled={busy} className="pill-btn pill-primary w-full justify-center" data-testid="apply-submit-btn">
                    <Send className="w-4 h-4"/> {busy ? "A enviar…" : "Enviar candidatura"}
                  </button>
                </form>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
